import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { DROPDOWN_CONFIG, DATATABLE_CONFIG, USER_SUBMENU_ID } from '../../../shared/Config';
import { HASACCESS } from '../../../shared/utility';

import EmployeeSalaryReportHeader from './EmployeeSalaryReportHeader';
import './EmployeeSalaryReport.scss';

// Dummy restaurants / employees — replace with API/Redux data
const RESTAURANTS = [
    { label: 'Chenthoor Restaurant', value: 'Chenthoor Restaurant' },
    { label: 'Chenthoor Restaurant - 2', value: 'Chenthoor Restaurant - 2' },
];

const ALL = 'ALL';
const EMPLOYEES = [
    { label: 'All Employees', value: ALL },
    ...['CHINESE MASTER', 'SOUTH INDIAN MASTER', 'PAROTA MASTER', 'PARCEL MAN', 'AUTO KUMAR'].map(e => ({ label: e, value: e })),
];

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const pad = (n) => String(n).padStart(2, '0');

// dd.mm.yy, same as the reference sheet
const formatDate = (d) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${String(d.getFullYear()).slice(-2)}`;

const formatNum = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

function generateDateRange(from, to) {
    const dates = [];
    const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    while (cur <= end) {
        dates.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return dates;
}

// Dummy employee salary — replace with API data: { date, employee, amount }
function buildRows(dates, employee) {
    const names = EMPLOYEES.slice(1).map(e => e.value).filter(n => employee === ALL || n === employee);
    const rows = [];
    dates.forEach((date) => {
        names.forEach((name, i) => {
            const seed = date.getDate() * 37 + (date.getMonth() + 1) * 11 + (EMPLOYEES.findIndex(e => e.value === name)) * 13;
            if ((seed + i) % 4 === 0) return; // not every employee sells every day
            rows.push({ id: rows.length, date, employee: name, amount: 50 + ((seed * 71) % 2000) });
        });
    });
    return rows;
}

const COLUMN_LABELS = ['DATE', 'EMPLOYEE', 'AMOUNT'];



class EmployeeSalaryReport extends Component {
    state = {
        Restaurant: RESTAURANTS[0].value,
        Employee: ALL,
        fromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        toDate: new Date(),
        tableData: [],
        searched: false,
        btnLoading: null, // key of the action currently running: search | showAll | clear | pdf | excel
    };

    componentDidMount() {
        this.getAccess = HASACCESS(USER_SUBMENU_ID);
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    // Same loader as Daily Sales Report (full-page .page-loader overlay); further clicks ignored meanwhile.
    runWithLoader = (key, action) => {
        if (this.state.btnLoading) return;
        this.setState({ btnLoading: key }, () => {
            setTimeout(() => {
                try {
                    action();
                } catch (e) {
                    this.growl && this.growl.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong. Please try again.' });
                } finally {
                    if (!this.unmounted) this.setState({ btnLoading: null });
                }
            }, 300);
        });
    };

    handleSearch = () => this.runWithLoader('search', this.handleSearchAction);
    handleShowAll = () => this.runWithLoader('showAll', this.handleShowAllAction);
    handleClear = () => this.runWithLoader('clear', this.handleClearAction);
    handleExportExcel = () => this.runWithLoader('excel', this.handleExportExcelAction);
    handleExportPdf = () => this.runWithLoader('pdf', this.handleExportPdfAction);

    handleSearchAction = () => {
        const { fromDate, toDate, Restaurant, Employee } = this.state;
        if (!Restaurant) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'Please select Restaurant' });
            return;
        }
        if (!fromDate || !toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'Please select From Date and To Date' });
            return;
        }
        if (fromDate > toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'From Date cannot be after To Date' });
            return;
        }
        this.setState({ tableData: buildRows(generateDateRange(fromDate, toDate), Employee), searched: true });
    };

    handleShowAllAction = () => {
        const now = new Date();
        const from = new Date(now.getFullYear(), now.getMonth(), 1);
        this.setState({ Employee: ALL, fromDate: from, toDate: now, tableData: buildRows(generateDateRange(from, now), ALL), searched: true });
    };

    handleClearAction = () => {
        const now = new Date();
        this.setState({ Restaurant: RESTAURANTS[0].value, Employee: ALL, fromDate: new Date(now.getFullYear(), now.getMonth(), 1), toDate: now, tableData: [], searched: false });
    };

    getTotal = () => round2(this.state.tableData.reduce((a, r) => a + Number(r.amount || 0), 0));

    reportTitle = () => {
        const { Restaurant, fromDate, toDate } = this.state;
        return `${String(Restaurant).toUpperCase()} - EMPLOYEE SALARY REPORT (${formatDate(fromDate)} to ${formatDate(toDate)})`;
    };

    buildExportMatrix = () => ({
        body: this.state.tableData.map(r => [formatDate(r.date), r.employee, r.amount]),
        total: ['TOTAL', '', this.getTotal()],
    });

    handleExportExcelAction = () => {
        const { body, total } = this.buildExportMatrix();
        if (body.length === 0) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'No data to export' });
            return;
        }
        const ws = XLSX.utils.aoa_to_sheet([[this.reportTitle()], COLUMN_LABELS, ...body, total]);
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COLUMN_LABELS.length - 1 } }];
        ws['!cols'] = [{ wch: 14 }, { wch: 30 }, { wch: 16 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Employee Salary Report');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'Employee_Salary_Report.xlsx');
    };

    handleExportPdfAction = () => {
        const { body, total } = this.buildExportMatrix();
        if (body.length === 0) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'No data to export' });
            return;
        }
        const fmt = (row) => row.map((v, i) => (i === 2 ? formatNum(v) : v));
        const doc = new jsPDF('p', 'mm', 'a4');
        doc.setFontSize(12);
        doc.text(this.reportTitle(), 14, 10);
        doc.autoTable({
            head: [COLUMN_LABELS],
            body: [...body.map(fmt), fmt(total)],
            startY: 15,
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 1.5, lineColor: 0, lineWidth: 0.2, textColor: 0 },
            columnStyles: { 0: { halign: 'center' }, 2: { halign: 'right' } },
            headStyles: { fillColor: [244, 176, 176], textColor: 0, halign: 'center' },
            didParseCell: (d) => {
                if (d.section === 'body' && d.row.index === body.length) d.cell.styles.fontStyle = 'bold';
            },
        });
        doc.save('Employee_Salary_Report.pdf');
    };

    render() {
        if (this.getAccess !== undefined && this.getAccess.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const { tableData, fromDate, toDate, searched, Restaurant, Employee, btnLoading } = this.state;
        const busy = btnLoading !== null;
        const center = { textAlign: 'center' };
        const right = { textAlign: 'right' };

        const headerGroup = (
            <ColumnGroup>
                <Row>
                    <Column header={this.reportTitle()} colSpan={3} style={{ textAlign: 'center', background: '#f4b0b0', color: '#000' }} />
                </Row>
                <Row>
                    <Column header="DATE" style={{ minWidth: '110px', ...center }} />
                    <Column header="EMPLOYEE" style={center} />
                    <Column header="AMOUNT" style={{ minWidth: '130px', ...center }} />
                </Row>
            </ColumnGroup>
        );

        const footerGroup = (
            <ColumnGroup>
                <Row>
                    <Column footer="TOTAL" colSpan={2} footerStyle={center} />
                    <Column footer={formatNum(searched ? this.getTotal() : 0)} footerStyle={right} />
                </Row>
            </ColumnGroup>
        );

        return (
            <div className="p-grid padding">
                {busy && <div className="page-loader"></div>}
                <Toast ref={(el) => this.growl = el} />

                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <EmployeeSalaryReportHeader />

                        <div className="p-grid p-align-end">
                            <div className="p-col-12 p-md-3 p-lg-3 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Restaurant</label>
                                <Dropdown
                                    placeholder="Restaurant"
                                    name="Restaurant"
                                    options={RESTAURANTS}
                                    value={Restaurant}
                                    onChange={(e) => this.setState({ Restaurant: e.value })}
                                    filter
                                    autoWidth
                                    className="p-col-12"
                                    {...DROPDOWN_CONFIG}
                                />
                            </div>

                            <div className="p-col-12 p-md-3 p-lg-3 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Employee</label>
                                <Dropdown
                                    placeholder="Employee"
                                    name="Employee"
                                    options={EMPLOYEES}
                                    value={Employee}
                                    onChange={(e) => this.setState({ Employee: e.value })}
                                    filter
                                    autoWidth
                                    className="p-col-12"
                                    {...DROPDOWN_CONFIG}
                                />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">From Date</label>
                                <Calendar placeholder="From Date" value={fromDate} dateFormat="dd/mm/yy" onChange={(e) => this.setState({ fromDate: e.value })} />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">To Date</label>
                                <Calendar placeholder="To Date" value={toDate} dateFormat="dd/mm/yy" onChange={(e) => this.setState({ toDate: e.value })} />
                            </div>

                            <div className="p-col-12 p-d-flex p-ai-center p-jc-between p-flex-wrap">
                                <div className="p-d-flex p-ai-center">
                                    <Button label="Search" icon={btnLoading === 'search' ? 'pi pi-spin pi-spinner' : 'pi pi-search'} className="p-button-primary" onClick={this.handleSearch} disabled={busy} />
                                    <Button label="Show All" icon={btnLoading === 'showAll' ? 'pi pi-spin pi-spinner' : 'pi pi-list'} className="p-button-secondary p-ml-2" onClick={this.handleShowAll} disabled={busy} />
                                    <Button label="Clear" icon={btnLoading === 'clear' ? 'pi pi-spin pi-spinner' : 'pi pi-times'} className="p-button-secondary p-ml-2" onClick={this.handleClear} disabled={busy} />
                                </div>
                                <div className="p-d-flex p-ai-center">
                                    <Button label="Export as Excel" icon={btnLoading === 'excel' ? 'pi pi-spin pi-spinner' : 'pi pi-file-excel'} className="p-button-warning" onClick={this.handleExportExcel} disabled={!searched || busy} />
                                    <Button label="Export as PDF" icon={btnLoading === 'pdf' ? 'pi pi-spin pi-spinner' : 'pi pi-file-pdf'} className="p-button-danger p-ml-2" onClick={this.handleExportPdf} disabled={!searched || busy} />
                                </div>
                            </div>
                        </div>

                        {searched && (
                            <div className="p-col-12" style={{ marginTop: '1em', overflowX: 'auto' }}>
                                <DataTable
                                    value={tableData}
                                    dataKey="id"
                                    headerColumnGroup={headerGroup}
                                    footerColumnGroup={footerGroup}
                                    scrollable
                                    scrollHeight="500px"
                                    style={{ width: '100%' }}
                                    emptyMessage="No records found"
                                    {...DATATABLE_CONFIG}
                                >
                                    <Column field="date" body={(r) => formatDate(r.date)} style={{ minWidth: '110px', ...center }} />
                                    <Column field="employee" />
                                    <Column field="amount" body={(r) => `₹ ${formatNum(r.amount)}`} style={{ minWidth: '130px', ...right }} />
                                </DataTable>
                            </div>
                        )}

                        {!searched && (
                            <div className="p-d-flex p-jc-center p-ai-center" style={{ minHeight: '200px', color: '#aaa', fontSize: '15px' }}>
                                <i className="pi pi-search p-mr-2" />
                                Select a date range and click Search to view the report.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    loggedUserDetail: state.auth.userObject,
});

export default connect(mapStateToProps)(EmployeeSalaryReport);
